/* rb-b-sidebar.js — Direction B behaviour. DESKTOP ONLY (>=981px).

   DEFAULT VARIANT IS 'style' — the treatment Mark picked. 'open' and 'mix' stay
   reachable with ?rbb=open / ?rbb=mix, remembered in sessionStorage because
   DynamicProductSearch rewrites the URL from the filter form on every change
   and drops unknown params.

   WHY IT RE-RUNS ON A MUTATION OBSERVER, NOT JUST ON LOAD
   DynamicProductSearch (theme.js ~3762) re-renders the whole section on every
   filter change with destination.innerHTML = source.innerHTML. Every element we
   touch is destroyed and rebuilt by that, so anything bound at load is gone
   after the first click. An observer on the section re-applies the treatment.
   See the PERFORMANCE section below for why that observer must be guarded.

   THE SORT PROXY, AND WHY THE REAL SELECT IS NOT MOVED
   #SortBy lives INSIDE .js-filters-form, in the sidebar. Moving that node into
   the bar takes it out of its form, and the theme submits the form to apply
   sorting — so it would silently stop working. Instead the bar gets a proxy;
   changing it writes into the real select and dispatches change.

   THE COUNT
   The theme renders no product count anywhere on the collection page. The
   loader section emits collection.products_count, correct on load but stale
   after an AJAX filter change, so it is re-read by fetching the current URL.
   On failure the count HIDES rather than showing a stale number.

   ============================ PERFORMANCE ==============================
   This file caused a visible scroll stutter on /collections/all. Two separate
   causes, both measured in Chrome with the Long Animation Frame API. Do not
   reintroduce either.

   1. LAYOUT READS ON THE SCROLL PATH. The old code measured the header on
      every scroll frame AND ran a 700ms rAF "settle" loop that measured it
      again. settle() 84-100ms (76-98ms forced layout), publishOffsets()
      58-104ms (53-73ms forced). Caching getComputedStyle was not enough: a
      SINGLE getBoundingClientRect cost ~50-70ms, because it forces a
      synchronous relayout of a 1900-product page that other scripts (lazy
      images, Clarity, the perf kit, product tracking) are dirtying
      continuously. The fix was to stop reading — see watchHeader().

   2. THE OBSERVER FIRING WHEN NOTHING WAS REBUILT. Lazy-loaded product images
      mutate the section too: 9 bursts during one ordinary scroll with no
      filter change. Every burst ran the whole treatment again — apply() cost
      295ms across 6 hits, 287ms of it forced layout, against a 16ms frame
      budget. The fix is rbbSwap.changed(), which compares element identity;
      see the observer in boot().

   AND ON MEASURING IT: headless Playwright reported a clean 17ms median and
   hid all of this. A requestAnimationFrame sampler cannot detect jank, because
   when the main thread stalls the sampler stalls with it and the positions it
   records still look perfectly smooth. Profile FRAME TIMING in a real browser.
   ======================================================================= */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 981px)';
  var GAP = 14;
  var SHRINK_AT = 300;
  var PEEK = 6;            // default rows shown before "See all"

  var VARIANTS = ['open', 'style', 'mix'];

  function variant() {
    var v = null;
    try {
      v = new URLSearchParams(window.location.search).get('rbb');
      if (v) sessionStorage.setItem('rbbVariant', v);
      else v = sessionStorage.getItem('rbbVariant');
    } catch (e) {}
    return VARIANTS.indexOf(v) !== -1 ? v : 'style';
  }

  function applyVariantClass() {
    var v = variant();
    var b = document.body.classList;
    b.toggle('rbb-v-open', v === 'open');
    b.toggle('rbb-v-style', v === 'style' || v === 'mix');
    b.toggle('rbb-v-mix', v === 'mix');
    return v;
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  /* ---------- header offset: ZERO LAYOUT READS ON SCROLL ----------
     The header is sticky WITH A TRANSFORM and slides completely off screen.
     Measured at 1440: scrollY 0 -> group bottom 137; scrollY 400+ -> bottom 0;
     height stays 102 throughout. So the panel follows the header's BOTTOM EDGE,
     not its height — a height-based offset pinned the column 116px down forever
     and left product images showing above the top of the filter.

     An IntersectionObserver with a dense threshold list does this without ever
     touching layout: entry.boundingClientRect is computed by the observer off
     the main thread, so reading it is free, and the callback fires as the
     header slides because its intersection ratio is changing. 41 thresholds
     over a 102px header is ~2.5px granularity — finer than the eye follows on
     a 300ms transition.

     RULE: no getBoundingClientRect, getComputedStyle, offsetTop or scrollHeight
     inside onScroll or anything it calls. window.pageYOffset is free. */
  var hdrEl = null;

  function headerGroup() {
    if (!hdrEl || !hdrEl.isConnected) {
      hdrEl = document.querySelector('.shopify-section-group-header-group');
    }
    return hdrEl;
  }

  var lastOffset = -1;

  function setOffset(h) {          // pure writer, touches no layout
    h = Math.max(0, Math.round(h));
    if (h === lastOffset) return;
    lastOffset = h;
    var root = document.documentElement;
    root.style.setProperty('--rbb-bar-top', h + 'px');
    root.style.setProperty('--rbb-top', (h + GAP) + 'px');
  }

  var hdrObs = null;

  function watchHeader() {
    var g = headerGroup();
    if (!g) return;
    if (!window.IntersectionObserver) {   // ancient browser: one read, at boot only
      setOffset(g.getBoundingClientRect().bottom);
      return;
    }
    if (hdrObs) hdrObs.disconnect();
    var th = [];
    for (var i = 0; i <= 40; i++) th.push(i / 40);
    hdrObs = new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      setOffset(e.boundingClientRect.bottom);   // free: the observer measured it
    }, { threshold: th });
    hdrObs.observe(g);                          // fires once immediately
  }

  /* ---------- count ---------- */
  var countCache = {};

  function readCountFrom(doc) {
    var el = doc.querySelector('[data-rbb-count]');
    if (!el) return null;
    var n = parseInt(el.getAttribute('data-rbb-count'), 10);
    return isNaN(n) ? null : n;
  }

  function label(n) {
    return n + (n === 1 ? ' product' : ' products');
  }

  function refreshCount(node) {
    var url = window.location.pathname + window.location.search;

    if (countCache[url] != null) {
      node.textContent = label(countCache[url]);
      node.hidden = false;
      return;
    }

    var inline = readCountFrom(document);
    if (inline != null && !window.__rbbSwapped) {
      countCache[url] = inline;
      node.textContent = label(inline);
      node.hidden = false;
      return;
    }

    node.hidden = true;   // never show a stale number while we find out
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var n = readCountFrom(new DOMParser().parseFromString(html, 'text/html'));
        if (n == null) return;
        countCache[url] = n;
        node.textContent = label(n);
        node.hidden = false;
      })
      .catch(function () { /* stays hidden, deliberately */ });
  }

  /* Pin (or un-pin) the column with inline styles — the stylesheet loses this
     fight, see the note at setPin's call site. Removed again below 981px so a
     desktop -> phone resize leaves the original layout as the theme wrote it. */
  function setPin(col, on) {
    var s = col.style;
    if (on) {
      s.setProperty('align-self', 'flex-start', 'important');
      s.setProperty('height', 'auto', 'important');
      s.setProperty('position', 'sticky', 'important');
      s.setProperty('top', 'var(--rbb-top, 116px)', 'important');
    } else {
      s.removeProperty('align-self');
      s.removeProperty('height');
      s.removeProperty('position');
      s.removeProperty('top');
    }
  }

  function teardown() {
    var col = document.querySelector('.rbb-col');
    if (col) { setPin(col, false); col.classList.remove('rbb-col'); }
    var lay = document.querySelector('.rbb-layout');
    if (lay) lay.classList.remove('rbb-layout');
    var bar = document.querySelector('.rbb-bar');
    if (bar) bar.classList.remove('rbb-bar', 'is-shrunk');
    document.body.classList.remove('rbb-on', 'rbb-v-open', 'rbb-v-style', 'rbb-v-mix');
  }

  /* ---------- facet search ---------- */
  var FIND_THRESHOLD = 12;

  function addFinder() {
    document.querySelectorAll('.collection-sidebar__filter-group').forEach(function (grp) {
      if (grp.classList.contains('collection-sidebar__filter-group--size')) return;

      var list = grp.querySelector('.collection-sidebar__items');
      if (!list) return;
      var rows = list.querySelectorAll('.collection-sidebar__item');
      if (rows.length <= FIND_THRESHOLD) return;
      if (grp.querySelector('.rbb-find')) return;

      var lbl = ((grp.querySelector('summary span') || {}).innerText || 'values').trim();
      var plural = lbl.toLowerCase();
      if (plural.slice(-1) !== 's') plural += 's';

      var input = document.createElement('input');
      input.type = 'search';
      input.className = 'rbb-find';
      input.autocomplete = 'off';
      input.placeholder = 'Search ' + rows.length + ' ' + plural;
      input.setAttribute('aria-label', 'Search ' + lbl);

      var note = document.createElement('p');
      note.className = 'rbb-empty';
      note.hidden = true;

      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var hits = 0;
        rows.forEach(function (row) {
          if (!q) { row.style.display = ''; hits++; return; }
          var t = (row.textContent || '').toLowerCase();
          var match = t.indexOf(q) !== -1;
          row.style.display = match ? '' : 'none';
          if (match) hits++;
        });
        note.textContent = 'Nothing matches “' + input.value.trim() + '”';
        note.hidden = !(q && hits === 0);
      });

      list.parentNode.insertBefore(input, list);
      list.parentNode.insertBefore(note, list.nextSibling);
    });
  }

  /* ---------- open the facets (open + mix only) ---------- */
  function openFacets(peek) {
    peek = peek || PEEK;
    document.querySelectorAll('.collection-sidebar__filter-group').forEach(function (grp) {
      if (grp.classList.contains('collection-sidebar__filter-group--size')) return;
      if (!grp.querySelector('.collection-sidebar__items')) return;
      grp.setAttribute('open', '');

      var rows = Array.prototype.slice.call(grp.querySelectorAll('.collection-sidebar__item'));
      if (rows.length <= peek) return;
      if (grp.querySelector('.rbb-more')) return;

      var expanded = false;
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'rbb-more';

      var paint = function () {
        rows.forEach(function (row, i) {
          var on = !!row.querySelector('input:checked');
          row.style.display = (expanded || i < peek || on) ? '' : 'none';
        });
        more.textContent = expanded ? 'Show fewer' : 'See all ' + rows.length;
      };

      more.addEventListener('click', function () { expanded = !expanded; paint(); });

      var list = grp.querySelector('.collection-sidebar__items');
      list.parentNode.insertBefore(more, list.nextSibling);
      paint();

      var find = grp.querySelector('.rbb-find');
      if (find) {
        find.addEventListener('input', function () {
          if (find.value.trim()) { more.hidden = true; }
          else { more.hidden = false; paint(); }
        });
      }
    });
  }

  /* ---------- remember which facets are open ----------
     Every filter click makes DynamicProductSearch re-render the section from
     Liquid, so the sidebar comes back at the theme's defaults. Whatever the
     shopper had arranged is wiped on every tag they pick.

     Keyed on the group's LABEL rather than its id, because the id is
     `{{ section.id }}-filter-{{ forloop.index }}` — positional, so it silently
     repoints at a different facet whenever Shopify returns the filters in a
     different order (which it does as counts change).

     The 'toggle' event does not bubble, so this listens in the CAPTURE phase. */
  var openState = {};
  var restoring = false;

  function groupKey(grp) {
    var s = grp.querySelector('summary span');
    return (((s && s.innerText) || grp.id || '') + '').trim().toLowerCase();
  }

  document.addEventListener('toggle', function (e) {
    if (restoring) return;
    var grp = e.target;
    if (!grp || !grp.classList) return;
    if (!grp.classList.contains('collection-sidebar__filter-group')) return;
    var k = groupKey(grp);
    if (k) openState[k] = grp.open;
  }, true);

  function restoreOpenState() {
    document.querySelectorAll('.collection-sidebar__filter-group').forEach(function (grp) {
      var k = groupKey(grp);
      if (!k || !(k in openState)) return;      // untouched facets keep theme defaults
      if (grp.open === openState[k]) return;
      restoring = true;
      grp.open = openState[k];
      restoring = false;
    });
  }

  /* ---------- the treatment ---------- */
  var applying = false;
  var barEl = null;

  function apply() {
    applying = true;
    try { applyInner(); }
    finally { setTimeout(function () { applying = false; }, 0); }
  }

  function applyInner() {
    if (!isDesktop()) { teardown(); return; }

    var sidebar = document.querySelector('.collection-sidebar');
    if (!sidebar) return;

    var col = sidebar.closest('.o-layout__item');
    var layout = col && col.parentElement;
    if (!col || !layout) return;

    document.body.classList.add('rbb-on');
    var v = applyVariantClass();
    layout.classList.add('rbb-layout');
    col.classList.add('rbb-col');

    /* ENFORCE THE PIN INLINE — the stylesheet alone loses this fight. The column
       computed align-self:stretch and height 5336.19px even with an !important
       rule at higher specificity, because something sets align-self:stretch on
       .o-layout__item and the page loads a dozen inline <style> blocks AFTER
       our asset, so ties go to them. A stretched flex item is as tall as the
       whole grid, and an element that tall can never appear to stick. */
    setPin(col, true);

    var bar = document.querySelector('.collection__filters-active');
    if (!bar) return;
    bar.classList.add('rbb-bar');
    barEl = bar;

    var wrap = bar.querySelector('.collection__filters-active__wrapper') || bar;

    var count = wrap.querySelector('.rbb-count');
    if (!count) {
      count = document.createElement('span');
      count.className = 'rbb-count';
      count.hidden = true;
      wrap.insertBefore(count, wrap.firstChild);
    }
    refreshCount(count);

    addFinder();
    if (v === 'open') openFacets(6);
    else if (v === 'mix') openFacets(4);

    // Last, so it wins over both the theme's defaults and openFacets().
    restoreOpenState();

    // sort proxy
    var real = document.querySelector('.js-collection-sort');
    if (real && !wrap.querySelector('.rbb-sort')) {
      var holder = document.createElement('div');
      holder.className = 'rbb-sort';

      var proxy = document.createElement('select');
      proxy.setAttribute('aria-label', 'Sort by');
      Array.prototype.forEach.call(real.options, function (o) {
        var opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = 'Sort · ' + o.textContent.trim();
        if (o.selected) opt.selected = true;
        proxy.appendChild(opt);
      });

      proxy.addEventListener('change', function () {
        var target = document.querySelector('.js-collection-sort');
        if (!target) return;
        target.value = proxy.value;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });

      holder.appendChild(proxy);
      wrap.appendChild(holder);
    }
  }

  /* ---------- condense on scroll ----------
     pageYOffset only. NO layout reads here — see PERFORMANCE at the top. */
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      if (!isDesktop()) return;
      if (!barEl || !barEl.isConnected) barEl = document.querySelector('.rbb-bar');
      if (barEl) barEl.classList.toggle('is-shrunk', window.pageYOffset > SHRINK_AT);
    });
  }

  /* ---------- boot ---------- */
  function boot() {
    watchHeader();
    apply();

    window.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener('resize', function () {
      lastOffset = -1;
      watchHeader();     // re-observe: the header's height may have changed
      if (window.rbbSwap) window.rbbSwap.invalidate('sidebar');
      apply();
    });
    window.addEventListener('orientationchange', function () {
      lastOffset = -1;
      watchHeader();
    });

    var section = document.querySelector('.js-section__collection');
    if (!section || !window.MutationObserver) return;

    /* `applying` hides our OWN writes from the observer — apply() edits the same
       subtree the observer watches, so without it the thing retriggers itself.

       rbbSwap.changed() is the separate, more important guard: it answers "was
       the section actually re-rendered?" by element identity. Without it this
       fired for every lazy-loaded product image (9 bursts in one ordinary
       scroll) and re-ran the whole treatment each time — 295ms across 6 hits,
       287ms of it forced layout. That was the stutter. */
    var pending = null;
    var obs = new MutationObserver(function () {
      if (applying) return;
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (window.rbbSwap && !window.rbbSwap.changed('sidebar')) return;
        window.__rbbSwapped = true;
        apply();
      }, 60);
    });
    obs.observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
